import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ColDef,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  RowClickedEvent,
  ValueFormatterParams
} from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { Dialog } from '@angular/cdk/dialog';
import { ProductLabelDialogService } from './product-label-dialog.service';
import { Subject, Subscription, finalize, takeUntil } from 'rxjs';
import { AuthService } from '../auth.service';
import {
  ProductOptions,
  ProductParams,
  ProductRow,
  ProductsService
} from '../products/products.service';
import { ProductPreviewDetailsDialogComponent } from './product-preview-details-dialog.component';

import { ProductCreateDialogComponent } from './product-create-dialog.component';

type Filters = {
  q: string;
  customer: string;
  warehouse: string;
  placement: string;
  available: string;
  delivered: string;
  created_from: string;
  created_to: string;
  intake_from: string;
  intake_to: string;
};
type SavedView = {
  filters?: Partial<Filters>;
  columns?: ColumnState[];
  columnFilters?: Record<string, unknown>;
  grouped?: boolean;
  pageSize?: number;
  theme?: 'dark' | 'light';
};
const emptyFilters = (): Filters => ({
  q: '',
  customer: '',
  warehouse: '',
  placement: '',
  available: '',
  delivered: '',
  created_from: '',
  created_to: '',
  intake_from: '',
  intake_to: ''
});

@Component({
  selector: 'app-products-preview',
  templateUrl: './products-preview.component.html',
  styleUrls: ['../products/products.component.scss']
})
export class ProductsPreviewComponent implements OnInit, OnDestroy {
  /* PROPERTIES */
  filters = emptyFilters();
  options: ProductOptions = { ok: true, customers: [], warehouses: [], export_limit: 20000 };
  theme: 'dark' | 'light' = 'dark';
  grouped = false;
  advanced = false;
  loading = false;
  optionsLoading = true;
  canCreate = false;
  exporting = false;
  error = '';
  optionsError = '';
  recordCount: number | null = null;
  updatedAt: Date | null = null;
  private api?: GridApi<ProductRow>;
  private destroyed = new Subject<void>();
  private requests = new Subscription();
  private storageKey = 'products-preview-view-v1';
  private saved: SavedView = {};
  private restoring = true;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private generation = 0;
  private pending = 0;
  defaultColDef: ColDef<ProductRow> = {
    sortable: true,
    resizable: true,
    minWidth: 110,
    filter: 'agTextColumnFilter',
    filterParams: { debounceMs: 500, maxNumConditions: 2 },
    floatingFilter: true,
    enableValue: false
  };
  columnDefs: ColDef<ProductRow>[] = [
    {
      headerName: 'Customer group',
      field: 'customer_key',
      hide: true,
      rowGroup: false,
      suppressColumnsToolPanel: true,
      filter: false
    },
    { headerName: 'Customer', field: 'customer_name', minWidth: 220, width: 250 },
    { headerName: 'Description', field: 'description', width: 290, tooltipField: 'description' },
    { headerName: 'Model', field: 'model', width: 180 },
    { headerName: 'Product code', field: 'product_code', width: 160 },
    { headerName: 'Serial', field: 'serial', width: 195 },
    { headerName: 'Barcode', field: 'barcode', width: 195 },
    {
      headerName: 'Warehouse',
      field: 'warehouse_name',
      width: 230,
      valueFormatter: (p) => (p.node?.group ? '' : p.value || 'No current spot recorded')
    },
    { headerName: 'Spot', field: 'spot', width: 240 },
    {
      headerName: 'Available',
      field: 'available',
      width: 130,
      filter: false,
      valueFormatter: this.booleanLabel,
      cellClass: (p) => (p.value === true ? 'pb-positive' : 'pb-muted')
    },
    // {
    //   headerName: 'Delivered',
    //   field: 'delivered',
    //   width: 130,
    //   filter: false,
    //   valueFormatter: this.booleanLabel,
    //   cellClass: (p) => (p.value === true ? 'pb-positive' : 'pb-muted')
    // },
    {
      headerName: 'Created',
      field: 'created',
      width: 175,
      filter: false,
      valueFormatter: (p) => this.dateLabel(p.value)
    },
    {
      headerName: 'Intake date',
      field: 'intake_date',
      width: 175,
      hide: true,
      filter: false,
      valueFormatter: (p) => this.dateLabel(p.value)
    },
    { headerName: 'PO', field: 'po_number', width: 155, hide: true },
    { headerName: 'SO', field: 'so_number', width: 155, hide: true },
    { headerName: 'ASN', field: 'asn_number', width: 165, hide: true },
    { headerName: 'BOL', field: 'bol_number', width: 170, hide: true },
    { headerName: 'Trip', field: 'trip_number', width: 145, hide: true },
    { headerName: 'Package ID', field: 'package_id', width: 190, hide: true },
    {
      headerName: 'Record type',
      field: 'record_kind',
      width: 145,
      hide: true,
      valueFormatter: (p) => (p.value === 'package' ? 'Package contents' : p.value ? 'Product' : '')
    },
    {
      headerName: 'Declared quantity',
      field: 'declared_quantity',
      width: 160,
      hide: true,
      filter: false
    },
    {
      headerName: '',
      colId: 'open',
      width: 210,
      minWidth: 210,
      pinned: 'right',
      sortable: false,
      filter: false,
      floatingFilter: false,
      suppressColumnsToolPanel: true,
      cellRenderer: (p: { node: { group?: boolean }; data?: ProductRow }) => {
        if (p.node.group || !p.data) {
          return '';
        }
        const labelState = p.data.package_uuid
          ? 'title="Preview and print this package label"'
          : 'disabled title="A package is required to print a label"';
        return `<div class="flex h-full items-center gap-2">
          <button type="button" class="pb-open" aria-label="View product details">View</button>
          <button type="button" data-product-action="label" ${labelState}
            class="inline-flex h-7 items-center gap-1.5 rounded-md border border-solid border-sky-500/20 bg-sky-500/10 px-2 text-xs font-semibold text-[var(--pb-accent)] disabled:cursor-not-allowed disabled:opacity-40">
            <i class="fa-solid fa-print" aria-hidden="true"></i> Print Label
          </button>
        </div>`;
      }
    }
  ];
  gridOptions: GridOptions<ProductRow> = {
    rowModelType: 'serverSide',
    pagination: true,
    paginationPageSize: 100,
    paginationPageSizeSelector: [25, 50, 100, 250],
    cacheBlockSize: 100,
    maxBlocksInCache: 4,
    maxConcurrentDatasourceRequests: 1,
    suppressAggFuncInHeader: true,
    rowHeight: 43,
    headerHeight: 43,
    animateRows: false,
    sideBar: { toolPanels: ['columns'], defaultToolPanel: undefined },
    getRowId: (p) => p.data.row_id || 'customer:' + p.data.customer_key,
    getChildCount: (row) => row.row_count,
    autoGroupColumnDef: {
      headerName: 'Customer',
      minWidth: 300,
      field: 'customer_name',
      filter: false,
      cellRendererParams: {
        suppressCount: false,
        innerRenderer: (p: { data?: ProductRow; value?: string }) => {
          const span = document.createElement('span');
          span.textContent = p.data?.customer_name || p.value || 'Unassigned';
          return span;
        }
      }
    },
    onSortChanged: () => this.save(),
    onFilterChanged: () => this.save(),
    onColumnMoved: () => this.save(),
    onColumnVisible: () => this.save(),
    onColumnResized: (e) => {
      if (e.finished) {
        this.save();
      }
    },
    onPaginationChanged: () => this.save()
  };

  /* FUNCTIONS */

  constructor(
    private readonly service: ProductsService,
    private readonly auth: AuthService,
    private readonly dialog: MatDialog,
    private readonly entryDialog: Dialog,
    private readonly labels: ProductLabelDialogService
  ) {}

  ngOnInit(): void {
    this.auth
      .ensureCurrentUserLoaded()
      .pipe(takeUntil(this.destroyed))
      .subscribe((user) => {
        this.storageKey = 'products-preview-view-v1-' + (user?.user_id ?? 'session');
        try {
          this.saved = JSON.parse(localStorage.getItem(this.storageKey) || '{}');
        } catch {
          this.saved = {};
        }
        this.filters = { ...emptyFilters(), ...(this.saved.filters || {}) };
        this.grouped = !!this.saved.grouped;
        this.theme =
          this.saved.theme ||
          (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
        this.loadOptions();
        this.service.creationOptions()
          .pipe(takeUntil(this.destroyed))
          .subscribe({
            next: (options) => { this.canCreate = options.can_create; },
            error: () => { this.canCreate = false; }
          });
      });
  }

  addProduct(): void {
    if (!this.canCreate) {
      return;
    }
    this.entryDialog
      .open<boolean>(ProductCreateDialogComponent, {
        width: '880px',
        maxWidth: '96vw',
        maxHeight: '90vh',
        ariaLabelledBy: 'product-create-title',
        data: { customer: this.filters.customer, theme: this.theme },
        disableClose: true
      })
      .closed.pipe(takeUntil(this.destroyed))
      .subscribe((saved) => {
        if (saved) {
          this.refresh();
          this.loadOptions();
        }
      });
  }

  loadOptions(): void {
    this.optionsLoading = true;
    this.optionsError = '';
    this.service
      .options()
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (response) => {
          this.options = response;
          this.optionsLoading = false;
        },
        error: (err) => {
          this.optionsLoading = false;
          this.optionsError =
            err.error?.message || 'Customer and warehouse choices could not load.';
        }
      });
  }

  onGridReady(event: GridReadyEvent<ProductRow>): void {
    this.api = event.api;
    if (this.saved.columns) {
      this.api.applyColumnState({ state: this.saved.columns, applyOrder: true });
    }
    if (this.saved.columnFilters) {
      this.api.setFilterModel(this.saved.columnFilters);
    }
    if ([25, 50, 100, 250].includes(this.saved.pageSize || 0)) {
      this.api.setGridOption('paginationPageSize', this.saved.pageSize!);
    }
    this.api.setRowGroupColumns(this.grouped ? ['customer_key'] : []);
    this.api.setColumnsVisible(['customer_key'], false);
    this.api.setGridOption('serverSideDatasource', this.datasource());
    this.restoring = false;
  }

  private datasource(): IServerSideDatasource {
    return {
      getRows: (params) => {
        let settled = false;
        const generation = this.generation;
        this.pending++;
        this.loading = true;
        this.error = '';
        const request = params.request;
        const query = this.query();
        query['offset'] = String(request.startRow || 0);
        query['limit'] = String(Math.min(250, (request.endRow || 100) - (request.startRow || 0)));
        query['sort'] = JSON.stringify(request.sortModel || []);
        query['filters'] = JSON.stringify(request.filterModel || {});
        if (request.rowGroupCols.length) {
          query['group'] = 'customer';
          if (request.groupKeys.length) {
            query['group_key'] = request.groupKeys[0];
          }
        }
        this.requests.add(
          this.service
            .list(query)
            .pipe(
              takeUntil(this.destroyed),
              finalize(() => {
                // A cancelled HTTP request must release AG Grid's datasource request slot.
                if (!settled) {
                  params.fail();
                }
              })
            )
            .subscribe({
              next: (result) => {
                if (generation !== this.generation) {
                  return;
                }
                settled = true;
                this.pending--;
                this.loading = this.pending > 0;
                if (!request.groupKeys.length) {
                  this.recordCount = result.record_count;
                }
                this.updatedAt = new Date();
                params.success({ rowData: result.data, rowCount: result.row_count });
              },
              error: (err) => {
                if (generation !== this.generation) {
                  return;
                }
                settled = true;
                this.pending--;
                this.loading = this.pending > 0;
                this.error = err.error?.message || 'Products could not load. Refresh to retry.';
                params.fail();
              }
            })
        );
      }
    };
  }

  searchChanged(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.refresh(), 450);
  }

  refresh(): void {
    clearTimeout(this.searchTimer);
    this.generation++;
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.pending = 0;
    this.loading = false;
    this.recordCount = null;
    this.error = '';
    this.save();
    this.api?.paginationGoToFirstPage();
    this.api?.setGridOption('serverSideDatasource', this.datasource());
  }

  toggleGrouping(): void {
    this.api?.setRowGroupColumns(this.grouped ? ['customer_key'] : []);
    this.api?.setColumnsVisible(['customer_key'], false);
    this.refresh();
  }

  toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    this.save();
  }

  reset(): void {
    this.restoring = true;
    this.filters = emptyFilters();
    this.grouped = false;
    this.api?.setFilterModel(null);
    this.api?.resetColumnState();
    this.api?.setRowGroupColumns([]);
    this.api?.setGridOption('paginationPageSize', 100);
    this.restoring = false;
    this.refresh();
  }

  rowClicked(event: RowClickedEvent<ProductRow>): void {
    if (!event.node.group && event.data) {
      const target = event.event?.target;
      if (target instanceof Element && target.closest('[data-product-action="label"]')) {
        if (event.data.package_uuid) {
          this.labels.open(event.data.package_uuid, this.theme);
        }
        return;
      }
      this.dialog.open(ProductPreviewDetailsDialogComponent, {
        data: { row: event.data, theme: this.theme },
        width: '1040px',
        maxWidth: '95vw',
        maxHeight: '92vh',
        autoFocus: false
      });
    }
  }

  get canExport(): boolean {
    return (
      this.recordCount !== null &&
      this.recordCount > 0 &&
      this.recordCount <= this.options.export_limit &&
      !this.exporting &&
      !this.loading
    );
  }

  get exportHint(): string {
    return this.recordCount !== null && this.recordCount > this.options.export_limit
      ? 'Filter to ' + this.options.export_limit.toLocaleString() + ' records or fewer to export.'
      : 'Export every matching record, including pages not loaded in the grid.';
  }

  exportCsv(): void {
    if (!this.canExport) {
      return;
    }
    this.exporting = true;
    const query = this.query();
    query['sort'] = JSON.stringify(
      (this.api?.getColumnState() || [])
        .filter((c) => c.sort)
        .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
        .map((c) => ({ colId: c.colId, sort: c.sort }))
    );
    query['filters'] = JSON.stringify(this.api?.getFilterModel() || {});
    this.service
      .export(query)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = 'products-' + new Date().toISOString().slice(0, 10) + '.csv';
          link.click();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
          this.exporting = false;
        },
        error: async (err) => {
          this.exporting = false;
          let message = 'Export failed. Narrow the filters and retry.';
          if (err.error instanceof Blob) {
            try {
              message = JSON.parse(await err.error.text()).message || message;
            } catch {}
          }
          this.error = message;
        }
      });
  }

  /* HELPER FUNCTIONS */

  private query(): ProductParams {
    return { ...this.filters };
  }

  private save(): void {
    if (this.restoring) {
      return;
    }
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          filters: this.filters,
          grouped: this.grouped,
          theme: this.theme,
          columns: this.api?.getColumnState(),
          columnFilters: this.api?.getFilterModel(),
          pageSize: this.api?.paginationGetPageSize()
        })
      );
    } catch {}
  }

  private booleanLabel(p: ValueFormatterParams): string {
    return p.node?.group ? '' : p.value === true ? 'Yes' : p.value === false ? 'No' : 'Unknown';
  }

  private dateLabel(value: unknown): string {
    return value ? String(value).replace('T', ' ').slice(0, 16) : '';
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimer);
    this.requests.unsubscribe();
    this.destroyed.next();
    this.destroyed.complete();
  }
}
